import { DiscoverChatbotProfileOverlay, type DiscoverChatbotProfileOverlaySelection, type ICharacter } from "@next-platform/client-common-core";
import React, { useCallback, useState } from "react";

import { BlockCreatorModal, CapabilityShareCharacterModal } from "@/components";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import DiscoverChatbotProfileOverlayView from "@/pages/Discover/components/DiscoverChatbotProfileOverlayView";

import useDiscoverChatbotProfileOverlayActions from "../hooks/useDiscoverChatbotProfileOverlayActions";

type DiscoverChatbotProfileOverlayHostProps = {
  selected: DiscoverChatbotProfileOverlaySelection | null;
  onClose: () => void;
  onLessLikeThis: (character: ICharacter) => void;
};

const DiscoverChatbotProfileOverlayHost = ({ selected, onClose, onLessLikeThis }: DiscoverChatbotProfileOverlayHostProps) => {
  const { hasFeatureFlag: isLorebookIndicatorEnabled } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_PUBLIC);

  const [stateIsShareModalOpen, setStateIsShareModalOpen] = useState(false);
  const [stateShareCharacter, setStateShareCharacter] = useState<{ id: string; name: string } | null>(null);

  const [stateIsBlockModalOpen, setStateIsBlockModalOpen] = useState(false);
  const [stateBlockCreator, setStateBlockCreator] = useState<{ id: string; username: string } | null>(null);

  const openShareModal = useCallback(
    (characterId: string) => {
      const name = selected?.preview?.name;

      if (characterId && name) {
        setStateShareCharacter({ id: characterId, name });
        setStateIsShareModalOpen(true);
      }
    },
    [selected?.preview?.name],
  );

  const openBlockModal = useCallback(
    (creatorUserId: string) => {
      const username = selected?.preview?.creator_username;

      if (creatorUserId && username) {
        setStateBlockCreator({ id: creatorUserId, username });
        setStateIsBlockModalOpen(true);
      }
    },
    [selected?.preview?.creator_username],
  );

  // The overlay only ever acts on the current selection, so the page handler takes the row character behind it.
  const handleLessLikeThis = useCallback(() => {
    if (selected) {
      onLessLikeThis(selected.preview);
    }
  }, [onLessLikeThis, selected]);

  const actions = useDiscoverChatbotProfileOverlayActions({ openShareModal, openBlockModal, onLessLikeThis: handleLessLikeThis, onClose });

  const handleClose = useCallback(() => {
    setStateIsShareModalOpen(false);
    setStateShareCharacter(null);

    setStateIsBlockModalOpen(false);
    setStateBlockCreator(null);

    onClose();
  }, [onClose]);

  const handleBlockModalClose = useCallback(() => {
    setStateIsBlockModalOpen(false);
  }, []);

  return (
    <>
      <DiscoverChatbotProfileOverlay
        isOpen={selected !== null}
        characterId={selected?.characterId ?? null}
        preview={selected?.preview ?? null}
        onClose={handleClose}
        onChatNow={actions.onChatNow}
        onFavorite={actions.onFavorite}
        onShare={actions.onShare}
        onCreatorPress={actions.onCreatorPress}
        onLorebookPress={actions.onLorebookPress}
        onBlockCreator={actions.onBlockCreator}
        onReport={actions.onReport}
        onLessLikeThis={actions.onLessLikeThis}
        isLorebookIndicatorEnabled={isLorebookIndicatorEnabled}
        view={DiscoverChatbotProfileOverlayView}
      />
      <CapabilityShareCharacterModal
        visible={stateIsShareModalOpen}
        setShowShare={setStateIsShareModalOpen}
        characterId={stateShareCharacter?.id}
        characterName={stateShareCharacter?.name}
      />
      {stateBlockCreator ? (
        <BlockCreatorModal
          creatorId={stateBlockCreator?.id}
          creatorUsername={stateBlockCreator?.username}
          isVisible={stateIsBlockModalOpen}
          onClose={handleBlockModalClose}
        />
      ) : null}
    </>
  );
};

export default React.memo(DiscoverChatbotProfileOverlayHost);
